import moment from 'moment-timezone';
import type AvailableTime from '../model/AvailableTime';
import type Specialist from '../model/Specialist';
import { expandTimeslotsForDay, ExpandedTimeslot, parseAppointmentLengthMinutes } from './expand-timeslots';
import { DATE_WITH_DASH, HOUR, MONTH } from '../constants/time-formats';

// "Next available" looks across the coming three months; the calendar itself can go further.
export const BOOKING_WINDOW_MONTHS = 3;

export interface BookableDay {
  date: string; // YYYY-MM-DD
  slots: ExpandedTimeslot[];
}

// Months are counted on the patient's clock: near a month end the browser's date can differ.
export const getWindowEnd = (now: moment.Moment, timezone: string) => (
  now.clone().tz(timezone).add(BOOKING_WINDOW_MONTHS, 'months')
);

// Calendar months, on the patient's clock, the availability API has to be asked for to cover the window.
export const getWindowMonths = (now: moment.Moment, timezone: string): string[] => {
  const months: string[] = [];
  const cursor = now.clone().tz(timezone).startOf('month');
  const end = getWindowEnd(now, timezone);
  while (cursor.isSameOrBefore(end, 'month')) {
    months.push(cursor.format(MONTH));
    cursor.add(1, 'month');
  }
  return months;
};

// Future slots (up to `until`, when given), grouped by day, earliest first. Days with no slot left are dropped.
// Slot times are on `timezone`, the clock the availability was requested in.
export const getBookableDays = (
  days: AvailableTime[],
  appointmentLength: string | undefined,
  now: moment.Moment,
  timezone: string,
  until?: moment.Moment,
): BookableDay[] => {
  const minutes = parseAppointmentLengthMinutes(appointmentLength);
  const byDate = new Map<string, ExpandedTimeslot[]>();

  days.forEach(({ date, availableTimeslots }) => {
    const slots = expandTimeslotsForDay(date, availableTimeslots, minutes).filter((slot) => {
      const start = moment.tz(`${date} ${slot.start}`, `${DATE_WITH_DASH} ${HOUR}`, timezone);
      return start.isAfter(now) && (!until || start.isSameOrBefore(until));
    });
    if (slots.length) {
      byDate.set(date, [...(byDate.get(date) ?? []), ...slots]);
    }
  });

  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, slots]) => ({
      date,
      slots: slots.sort((a, b) => a.start.localeCompare(b.start)),
    }));
};

export interface SpecialtyOpening {
  specialist: Specialist;
  date: string;
}

// The soonest bookable day across the given clinicians, or null if none has one.
export const findNextOpening = (
  specialists: Specialist[],
  bookableDaysById: Record<string, BookableDay[] | undefined>,
): SpecialtyOpening | null => specialists.reduce<SpecialtyOpening | null>((soonest, specialist) => {
  const first = bookableDaysById[specialist.id]?.[0];
  if (!first || (soonest && soonest.date <= first.date)) {
    return soonest;
  }
  return { specialist, date: first.date };
}, null);

export const formatShortDate = (date: string) => moment(date, DATE_WITH_DASH).format('ddd D MMM');
export const formatLongDate = (date: string) => moment(date, DATE_WITH_DASH).format('dddd D MMM');
export const formatSlotTime = (time: string) => moment(time, HOUR).format('h:mm A');

export type TimeSegment = 'morning' | 'afternoon' | 'evening';

export const TIME_SEGMENTS: { key: TimeSegment, label: string }[] = [
  { key: 'morning', label: 'Morning' },
  { key: 'afternoon', label: 'Afternoon' },
  { key: 'evening', label: 'Evening' },
];

// As in the ADHD portal: before 12:00 is morning, before 18:00 afternoon, then evening.
export const getTimeSegment = (time: string): TimeSegment => {
  const hour = Number(time.slice(0, 2));
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
};

// A day's slots grouped by part of the day, keeping only the parts that have slots.
export const groupSlotsBySegment = (slots: ExpandedTimeslot[]) => TIME_SEGMENTS
  .map(({ key, label }) => ({ key, label, slots: slots.filter(({ start }) => getTimeSegment(start) === key) }))
  .filter(({ slots: inSegment }) => inSegment.length > 0);

// Clinicians with a time first, soonest first; then those still loading; then those with none.
// Within each, alphabetical by first name.
export const sortByNextAvailable = <T extends { id: string, firstName?: string }>(
  clinicians: T[],
  firstDate: (id: string) => string | null | undefined,
): T[] => {
  const rank = ({ id }: T) => {
    const date = firstDate(id);
    if (date) return `0${date}`;
    return date === undefined ? '1' : '2';
  };
  const byName = (a: T, b: T) => (a.firstName ?? '').localeCompare(b.firstName ?? '', 'en', { sensitivity: 'base' });
  return clinicians
    .map((clinician) => ({ clinician, key: rank(clinician) }))
    .sort((a, b) => a.key.localeCompare(b.key) || byName(a.clinician, b.clinician))
    .map(({ clinician }) => clinician);
};
